/**
 * UpdateAvailableBanner — compact floating card that surfaces auto-update
 * lifecycle progress to the user (M5 / Step 8).
 *
 * Renders nothing on web (`!updates.enabled`) and nothing on desktop while
 * the updater is `idle` or `not-available`. Shows a small download-progress
 * card while downloading and a prominent "Restart now" card once the update
 * is fully on disk.
 *
 * Mounted bottom-left inside the sidebar's empty space (above the company
 * logo) by Layout.tsx — a compact card, not a full-width top bar, so it
 * never overlaps the header/logo.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useUpdateStatus } from '../hooks/useUpdateStatus';

// Shared compact-card shell. `tone` drives the accent color.
const CardShell: React.FC<{
  tone: 'success' | 'info' | 'warning';
  onDismiss: () => void;
  children: React.ReactNode;
}> = ({ tone, onDismiss, children }) => {
  const accent =
    tone === 'success' ? '#1F7A63' : tone === 'warning' ? '#B45309' : '#0F766E';
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '8px',
        p: 1.25,
        bgcolor: '#ffffff',
        border: `1px solid ${accent}`,
        borderLeft: `4px solid ${accent}`,
        boxShadow: '0 10px 28px rgba(15,23,42,0.18)',
      }}
    >
      <IconButton
        size="small"
        onClick={onDismiss}
        aria-label="Dismiss update notification"
        sx={{ position: 'absolute', top: 2, right: 2, color: 'var(--text-muted)', p: 0.3 }}
      >
        <CloseIcon sx={{ fontSize: 15 }} />
      </IconButton>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75, pr: 2 }}>
        <SystemUpdateAltIcon sx={{ fontSize: 17, color: accent }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: accent, lineHeight: 1.2 }}>
          {tone === 'success' ? 'Update ready' : tone === 'warning' ? 'Update check failed' : 'Updating'}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
};

export const UpdateAvailableBanner: React.FC = () => {
  const { status, enabled, applyUpdate } = useUpdateStatus();
  const [dismissed, setDismissed] = useState(false);
  const [restarting, setRestarting] = useState(false);

  if (!enabled) return null;

  const showBanner =
    status.phase === 'available' ||
    status.phase === 'downloading' ||
    status.phase === 'downloaded' ||
    (status.phase === 'error' && !!status.message);

  if (!showBanner || dismissed) return null;

  const handleRestart = async (): Promise<void> => {
    setRestarting(true);
    try {
      await applyUpdate();
    } catch {
      setRestarting(false);
    }
  };

  const dismiss = () => setDismissed(true);

  if (status.phase === 'error') {
    return (
      <Collapse in={!dismissed}>
        <CardShell tone="warning" onDismiss={dismiss}>
          <Typography sx={{ fontSize: '0.68rem', color: 'var(--text-secondary)', lineHeight: 1.35 }}>
            Couldn&apos;t check for updates. We&apos;ll try again shortly.
          </Typography>
        </CardShell>
      </Collapse>
    );
  }

  if (status.phase === 'downloaded') {
    return (
      <Collapse in={!dismissed}>
        <CardShell tone="success" onDismiss={dismiss}>
          <Typography sx={{ fontSize: '0.68rem', color: 'var(--text-secondary)', lineHeight: 1.35, mb: 1 }}>
            Forge Halima {status.version ? `v${status.version} ` : ''}is ready. Restart to apply.
          </Typography>
          <Button
            fullWidth
            size="small"
            startIcon={restarting ? <CircularProgress size={13} color="inherit" /> : <RestartAltIcon sx={{ fontSize: 16 }} />}
            disabled={restarting}
            onClick={handleRestart}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.72rem',
              py: 0.5,
              bgcolor: '#1F7A63',
              color: '#fff',
              '&:hover': { bgcolor: '#186251' },
            }}
          >
            Restart now
          </Button>
        </CardShell>
      </Collapse>
    );
  }

  // available / downloading
  const percent = Math.max(0, Math.min(100, Math.round(status.percent ?? 0)));
  return (
    <Collapse in={!dismissed}>
      <CardShell tone="info" onDismiss={dismiss}>
        <Typography sx={{ fontSize: '0.68rem', color: 'var(--text-secondary)', lineHeight: 1.35, mb: 0.75 }} noWrap>
          Downloading update{status.version ? ` v${status.version}` : ''}…
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <LinearProgress
            variant={status.phase === 'downloading' ? 'determinate' : 'indeterminate'}
            value={percent}
            sx={{ flex: 1, height: 6, borderRadius: 2 }}
          />
          <Typography sx={{ fontSize: '0.64rem', color: 'var(--text-muted)', minWidth: 30, textAlign: 'right' }}>
            {status.phase === 'downloading' ? `${percent}%` : ''}
          </Typography>
        </Stack>
      </CardShell>
    </Collapse>
  );
};

export default UpdateAvailableBanner;
